import { createInertiaApp } from "@inertiajs/react";
import { type ReactNode, createElement } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

import { initializeTheme } from "@/hooks/use-appearance";
import PersistentLayout from "@/layouts/persistent-layout";
import { preloadImages } from "@/lib/image-preload";
import type { GalleryImageSummary } from "@/types";

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode };
}

const appName = import.meta.env.VITE_APP_NAME ?? "OG Pilot";

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= (page) => createElement(PersistentLayout, null, page);

    return page;
  },

  setup({ el, App, props }) {
    // data-seo-fallback is crawlable HTML for when SSR does not return a body.
    // It is not a React tree, so replace it instead of hydrating it.
    const isSeoFallback = el.dataset.seoFallback === "true";
    if (el.hasChildNodes() && !isSeoFallback) {
      hydrateRoot(el, createElement(App, props));
      return;
    }
    createRoot(el).render(createElement(App, props));
  },

  progress: {
    color: "#f54a00",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    );
  }
});

// This will set light / dark mode on load...
initializeTheme();

// Preload critical gallery images (first 3 images that appear in carousel)
const initialGallerySources = (() => {
  if (typeof document === "undefined") return [];
  const root = document.getElementById("app");
  if (!root) return [];

  try {
    const pageData = root.dataset.page
      ? (JSON.parse(root.dataset.page) as {
          props: { gallery_images?: GalleryImageSummary[] };
        })
      : { props: {} };
    const images = pageData?.props?.gallery_images;
    if (Array.isArray(images)) {
      return images.slice(0, 3).map((image: { src?: string }) => image?.src);
    }
  } catch (error) {
    console.warn("Unable to parse initial gallery images", error);
  }

  return [];
})().filter((src): src is string => typeof src === "string" && src.length > 0);

if (initialGallerySources.length > 0) {
  preloadImages(initialGallerySources);
}

// Track initial page view and subsequent Inertia navigations with Ahoy and PostHog
// Only runs client-side (not during SSR)
if (typeof window !== "undefined") {
  interface InertiaNavigateDetail {
    page?: { component?: string };
  }

  const trackPageView = async (event?: CustomEvent<InertiaNavigateDetail>) => {
    const page = event && "detail" in event ? event.detail?.page : undefined;
    const component = page?.component;
    window.posthog?.capture("$pageview", {
      path: window.location.pathname,
      ...(component ? { component } : {}),
    });

    try {
      // Dynamic import ensures ahoy only loads in browser
      const { default: ahoy } = await import("ahoy.js");
      ahoy.track("page_view", {
        url: window.location.href,
        path: window.location.pathname,
        title: document.title,
        component,
      });
    } catch {
      // no-op
    }
  };

  // Initial view after app boot
  void trackPageView();

  // Track on every Inertia navigation
  document.addEventListener("inertia:navigate", (event) => {
    void trackPageView(event as CustomEvent<InertiaNavigateDetail>);
  });
}
